import { Typography } from "@suraasa/placebo-ui"
import React from "react"
import { formatNumberWithCommas, pluralize } from "utils/helpers"
import { useResource } from "utils/hooks/useResource"
import { useUserLeadContext } from "views/leadGrowthForm/Context"

interface DetailCardProps {
  title: string
  children: React.ReactNode
}

const DetailCard: React.FC<DetailCardProps> = ({ title, children }) => (
  <div className="flex w-full flex-col gap-2 rounded-md border-[1.5px] border-primary-100 bg-primary-50 p-2">
    <Typography variant="title3" className="!tracking-wide text-primary-700">
      {title}
    </Typography>
    {children}
  </div>
)

interface DetailRowProps {
  label: string
  value: string
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <div className="gap-2">
    <Typography variant="preTitle" className="uppercase text-primary-600">
      {label}
    </Typography>
    <Typography variant="strong" className="text-primary-800">
      {value}
    </Typography>
  </div>
)

const FormDetails = () => {
  const { leadProfile } = useUserLeadContext()

  const { subjects } = useResource(["subjects"])

  const getSubject = (id: string | null) =>
    subjects.find(x => x.uuid === id)?.name

  const {
    desiredOutcome,
    dateOfBirth,
    desiredSalary,
    desiredSalaryCurrency,
    curriculumsTaught,
    city,
    country,
    primaryGoal,
    qualifications,
    yearsOfExperience,
    userLead,
  } = leadProfile || {}

  return (
    <div>
      <Typography variant="body" className="mb-2 text-primary-800">
        {userLead?.fullName}, here&apos;s a snapshot of your profile based on
        the information you shared:
      </Typography>

      <div className="flex flex-col gap-0.75 md:flex-row">
        <DetailCard title="Personal Details">
          <DetailRow
            label="Location"
            value={[city, country].filter(Boolean).join(", ")}
          />
          <DetailRow
            label="Date of Birth"
            value={
              dateOfBirth
                ? new Date(dateOfBirth).toLocaleDateString("en-CA", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "-"
            }
          />
        </DetailCard>

        <DetailCard title="Professional Background">
          <DetailRow
            label="Qualifications"
            value={
              qualifications && qualifications.length > 0
                ? qualifications
                    .map(qualification =>
                      qualification.subjectId
                        ? [
                            qualification.name || "Qualification",
                            getSubject(qualification.subjectId),
                          ]
                            .filter(Boolean)
                            .join(" in ")
                        : qualification.name || ""
                    )
                    .join(", ")
                : "-"
            }
          />
          <DetailRow
            label="Experience as School Teacher"
            value={
              yearsOfExperience
                ? pluralize(" year", yearsOfExperience, {
                    plural: " years",
                  })
                : "-"
            }
          />
          <DetailRow
            label="Board/Curriculum Taught"
            value={curriculumsTaught ? curriculumsTaught.join(", ") : "-"}
          />
        </DetailCard>

        <DetailCard title="Career Aspirations">
          <DetailRow label="Outcome Bucket" value={desiredOutcome ?? "-"} />
          <DetailRow
            label="Target Salary"
            value={
              desiredSalary != null && desiredSalaryCurrency != null
                ? `${desiredSalaryCurrency} ${formatNumberWithCommas(
                    Number(desiredSalary)
                  )} per year`
                : "-"
            }
          />
          <DetailRow
            label="Primary Goal"
            value={primaryGoal != null ? primaryGoal : "-"}
          />
        </DetailCard>
      </div>
    </div>
  )
}

export default FormDetails
